// src/components/practice/aiFeedbackMessages.js
//
// Pure, framework-free helpers for the essay feedback tiers (L3). Maps gradeEssay
// failure reasons to calm, specific, wellbeing-toned copy (NEVER raw codes), turns
// a graded result into plain-language display strings, and provides a stable input
// hash so we never auto-re-grade identical input. JSX-free -> node:test-able.

// Calm failure copy. The essay is NEVER cleared on any of these; the Tutor Review
// option stays available. Wording matches the L3 spec exactly.
import { markSheetCriteria, markSheetMoves } from './markSheetView.js';
import { hasUnreadableFeedback, UNAVAILABLE_FEEDBACK_NOTICE } from '../../utils/feedbackText.js';
import { stripDanielleReviewNotice, withoutPendingReviewNotes } from '../../../functions/src/ai/reviewNotice.js';

export const FAILURE_MESSAGES = {
    source_disclosure_limited: 'The AI response copied too much reference material, so it was not shown. Your saved draft is safe. Ask for focused feedback or request a Tutor Review.',
    draft_save_failed: "We couldn't confirm your draft was saved, so AI marking hasn't started. Keep this page open, copy your answer somewhere safe, then try saving again.",
    provider_error: 'The AI service could not complete this request. Your saved draft is separate from AI feedback. Please try again later.',
    email_unverified: 'Verify your email, then start your two-mark trial from My Essays.',
    trial_locked: 'Start your trial from My Essays. If both trial marks are used, unlock a study pack to continue.',
    not_entitled: 'AI feedback requires active access to this pack. Unlock the pack first, or contact us if this seems wrong.',
    not_configured: "AI feedback isn't available for this question yet. You can still request a Tutor Review.",
    materials_unavailable: "We couldn't load this pack's teaching materials. Your feedback allowance hasn't been used. Please try again later.",
    rate_limited: "You've used AI feedback several times recently. Try again later -- keep editing meanwhile.",
    regen_exhausted: "You've already regenerated feedback for this essay. Edit your essay to get fresh feedback, or request a Tutor Review.",
    token_limited: "You've reached today's AI feedback budget. It resets tomorrow -- or request a Tutor Review meanwhile.",
    oversized: 'This essay is too long for instant AI feedback. Shorten it, or request a Tutor Review quote.',
    config_error: 'AI feedback is temporarily unavailable for this question. Try another question or request a Tutor Review.',
    malformed_model_output: "We couldn't generate reliable AI feedback this time. Try again, or request a Tutor Review.",
    unavailable: "AI feedback is temporarily unavailable. Your essay hasn't been lost -- please try again later.",
};

export function diagnosticReference(value) {
    return typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value) ? value : '';
}

// Unknown / missing reasons fall back to the neutral "temporarily unavailable".
export function failureMessage(reason) {
    return FAILURE_MESSAGES[reason] || FAILURE_MESSAGES.unavailable;
}

// Plain-language confidence (R2-b): a range is a guide, not a verdict. Low
// confidence gently funnels toward a human Tutor Review.
export function confidencePlain(confidence) {
    if (confidence === 'high') return { label: 'High', note: 'this draft sits fairly clearly in this range' };
    if (confidence === 'low') return { label: 'Low', note: 'hard to place from this draft alone -- a Tutor Review will give a firmer mark' };
    // default + 'medium'
    return { label: 'Medium', note: 'shows features of more than one band -- treat it as a guide' };
}

// Compact band-range display. "Level 3"/"Level 4" -> "Level 3-4"; same low/high ->
// the single band; other vocabularies -> "low - high". mostLikely appended if set.
export function formatBandRange(range, mostLikelyBand) {
    if (!range || !range.low || !range.high) return '';
    const { low, high } = range;
    let base;
    const ml = /^Level (\d+)$/.exec(low);
    const mh = /^Level (\d+)$/.exec(high);
    if (low === high) base = low;
    else if (ml && mh) base = 'Level ' + ml[1] + '-' + mh[1];
    else base = low + ' - ' + high;
    // Skip the suffix when it adds nothing (single-band range equal to the most
    // likely band -- the common case under the server-computed marks contract).
    if (mostLikelyBand && mostLikelyBand !== base) base += ' (most likely ' + mostLikelyBand + ')';
    return base;
}

// Compact AO-marks line for the server-computed marks contract. Prefers the
// self-describing aoMarks array (per-pack config contract); falls back to the
// legacy flat ao4/ao5 fields (records persisted by the interim 45-mark
// contract); returns null for band-only results -- which render exactly as
// before, with no fabricated numbers.
export function formatAoMarks(fb) {
    if (!fb) return null;
    if (Array.isArray(fb.aoMarks) && fb.aoMarks.length > 0
        && fb.aoMarks.every((a) => a && Number.isInteger(a.mark) && Number.isInteger(a.maxMark) && Number.isInteger(a.level))
        && Number.isInteger(fb.totalMark) && Number.isInteger(fb.totalMax)) {
        return fb.aoMarks
            .map((a) => (a.label || a.key || 'AO') + ' ' + a.mark + '/' + a.maxMark + ' (Level ' + a.level + ')')
            .join(' · ')
            + ' · Total ' + fb.totalMark + '/' + fb.totalMax;
    }
    const nums = [fb.ao4Mark, fb.ao4Level, fb.ao5Mark, fb.ao5Level, fb.totalMark];
    if (!nums.every((n) => Number.isInteger(n))) return null;
    return 'AO4 ' + fb.ao4Mark + '/27 (Level ' + fb.ao4Level + ') · '
        + 'AO5 ' + fb.ao5Mark + '/18 (Level ' + fb.ao5Level + ') · '
        + 'Total ' + fb.totalMark + '/45';
}

// Stable, compact hash (djb2) of the exact grading INPUT, so the UI can tell
// "same essay -> regenerate is a deliberate redo" from "essay edited -> result may
// be stale" without re-calling the model automatically.
export function hashEssay(packId, promptId, essayText) {
    const str = [String(packId || ''), String(promptId || ''), String(essayText || '')].join('\n');
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return String(h >>> 0);
}

// Plain-text rendering of a graded result for the "Copy feedback" button.
export function gradingMaterialLines(materials) {
    if (materials?.version !== 1) return [];
    const lines = ['Pack materials supplied' + (materials.packTitle ? ': ' + materials.packTitle : '')];
    const labels = { original_text: 'source text stored in pack', reference_model: 'reference model / template; not original text',
        teaching_notes: 'teaching notes / model responses', mark_scheme: 'mark scheme; task match needs checking',
        question_paper: 'question paper', exam_specification: 'exam specification', editorial_context: 'publisher / editorial context; not author text' };
    for (const s of (Array.isArray(materials.sources) ? materials.sources : []).slice(0, 12)) {
        if (!s || !labels[s.kind]) continue;
        lines.push('- ' + (s.title || 'Untitled material') + ' ('
            + labels[s.kind] + (s.paperKind === 'sample' ? '; sample, not past exam' : '')
            + (s.range ? '; ' + s.range : '')
            + (s.partial ? '; shortened' : '') + ')');
    }
    if (materials.note) { const note = stripDanielleReviewNotice(materials.note); if (note) lines.push(note); }
    return lines;
}

export function buildCopyText(result) {
    result = withoutPendingReviewNotes(result);
    if (!result || result.status !== 'graded') return '';
    const lines = [];
    lines.push('AI formative feedback');
    const marks = formatAoMarks(result);
    if (marks) lines.push(marks);
    if (result.assessmentLabel) lines.push(result.assessmentLabel);
    if (result.assessmentNote) lines.push(result.assessmentNote);
    if (result.indicativeBandRange?.low) lines.push('Indicative band: ' + formatBandRange(result.indicativeBandRange, result.mostLikelyBand));
    if (result.profileNote) lines.push(result.profileNote);
    const conf = confidencePlain(result.confidence);
    lines.push('Confidence: ' + conf.label + ' (' + conf.note + ')');
    lines.push('');
    if (hasUnreadableFeedback(result)) lines.push(UNAVAILABLE_FEEDBACK_NOTICE);
    for (const c of markSheetCriteria(result)) {
        lines.push('- ' + (c.name || '') + ': ' + (c.comment || ''));
        if (c.evidence) lines.push('  Evidence: ' + c.evidence);
    }
    lines.push('');
    lines.push('Next steps:');
    for (const s of markSheetMoves(result)) lines.push('- ' + s);
    const materials = gradingMaterialLines(result.gradingMaterials);
    if (materials.length) lines.push('', ...materials);
    if (result.disclaimer) { lines.push(''); lines.push(result.disclaimer); }
    return lines.join('\n');
}
